import { supabase } from '../lib/supabase';
import { subDays, format, differenceInHours, differenceInDays } from 'date-fns';

export const metricsService = {
  /**
   * Get submission trends over time (daily aggregation)
   */
  async getSubmissionTrends(practitionerId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase?.from('referrals')?.select('submission_date, status')?.gte('submission_date', startDate?.toISOString());

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group by day
      const trendsByDay = {};
      data?.forEach(referral => {
        const day = format(new Date(referral.submission_date), 'yyyy-MM-dd');
        if (!trendsByDay?.[day]) {
          trendsByDay[day] = {
            date: day,
            total: 0,
            urgent: 0,
            routine: 0,
            emergency: 0
          };
        }
        trendsByDay[day].total++;
        trendsByDay[day][referral.urgency]++;
      });

      // Fill missing days with zeros
      const trends = [];
      for (let i = 0; i < days; i++) {
        const day = format(subDays(new Date(), days - i - 1), 'yyyy-MM-dd');
        trends?.push(trendsByDay?.[day] || {
          date: day,
          total: 0,
          urgent: 0,
          routine: 0,
          emergency: 0
        });
      }

      return trends;
    } catch (error) {
      console.error('Error fetching submission trends:', error);
      throw error;
    }
  },

  /**
   * Get pending cases analysis
   */
  async getPendingCasesAnalysis(practitionerId) {
    try {
      let query = supabase?.from('referrals')?.select('status, urgency, submission_date')?.in('status', ['new', 'reviewed']);

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const now = new Date();
      const analysis = {
        total: data?.length || 0,
        byStatus: {
          new: 0,
          reviewed: 0
        },
        byUrgency: {
          routine: 0,
          urgent: 0,
          emergency: 0
        },
        byAge: {
          lessThan24h: 0,
          between24hAnd3d: 0,
          between3dAnd7d: 0,
          moreThan7d: 0
        },
        oldestPending: null
      };

      data?.forEach(referral => {
        // By status
        analysis.byStatus[referral.status]++;
        
        // By urgency
        analysis.byUrgency[referral.urgency]++;
        
        // By age
        const submissionDate = new Date(referral.submission_date);
        const hoursOld = differenceInHours(now, submissionDate);
        
        if (hoursOld < 24) {
          analysis.byAge.lessThan24h++;
        } else if (hoursOld < 72) {
          analysis.byAge.between24hAnd3d++;
        } else if (hoursOld < 168) {
          analysis.byAge.between3dAnd7d++;
        } else {
          analysis.byAge.moreThan7d++;
        }
      });

      // Find oldest pending
      if (data?.length > 0) {
        analysis.oldestPending = {
          id: data?.[0]?.id,
          patientName: `${data?.[0]?.patient_first_name} ${data?.[0]?.patient_last_name}`,
          daysOld: differenceInDays(now, new Date(data[0].submission_date)),
          urgency: data?.[0]?.urgency
        };
      }

      return analysis;
    } catch (error) {
      console.error('Error fetching pending cases analysis:', error);
      throw error;
    }
  },

  /**
   * Get practitioner performance metrics
   */
  async getPractitionerPerformance(practitionerId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase?.from('referrals')?.select('referring_dentist_name, status, submission_date, reviewed_at, completed_at')?.gte('submission_date', startDate?.toISOString());

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group by referring dentist
      const dentistStats = {};
      data?.forEach(referral => {
        const dentist = referral?.referring_dentist_name;
        if (!dentistStats?.[dentist]) {
          dentistStats[dentist] = {
            name: dentist,
            totalReferrals: 0,
            completed: 0,
            pending: 0,
            cancelled: 0,
            avgResponseTime: 0,
            responseTimes: []
          };
        }
        
        dentistStats[dentist].totalReferrals++;
        
        if (referral?.status === 'completed') {
          dentistStats[dentist].completed++;
        } else if (referral?.status === 'new' || referral?.status === 'reviewed') {
          dentistStats[dentist].pending++;
        } else if (referral?.status === 'cancelled') {
          dentistStats[dentist].cancelled++;
        }
        
        // Calculate response time (submission to first review)
        if (referral?.reviewed_at) {
          const responseTime = differenceInHours(
            new Date(referral.reviewed_at),
            new Date(referral.submission_date)
          );
          dentistStats?.[dentist]?.responseTimes?.push(responseTime);
        }
      });

      // Calculate average response times
      Object.values(dentistStats)?.forEach(stats => {
        if (stats?.responseTimes?.length > 0) {
          stats.avgResponseTime = 
            stats?.responseTimes?.reduce((a, b) => a + b, 0) / stats?.responseTimes?.length;
        }
        delete stats?.responseTimes; // Remove raw data
      });

      // Sort by total referrals
      const topDentists = Object.values(dentistStats)?.sort((a, b) => b?.totalReferrals - a?.totalReferrals)?.slice(0, 10);

      return {
        topDentists,
        totalUniqueDentists: Object.keys(dentistStats)?.length,
        totalReferrals: data?.length || 0
      };
    } catch (error) {
      console.error('Error fetching practitioner performance:', error);
      throw error;
    }
  },

  /**
   * Get response time metrics
   */
  async getResponseTimeMetrics(practitionerId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase?.from('referrals')?.select('submission_date, reviewed_at, scheduled_at, completed_at, status')?.gte('submission_date', startDate?.toISOString());

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const responseTimes = [];
      const timeToSchedule = [];
      const timeToComplete = [];

      data?.forEach(referral => {
        // Time to first review
        if (referral?.reviewed_at) {
          const hours = differenceInHours(
            new Date(referral.reviewed_at),
            new Date(referral.submission_date)
          );
          responseTimes?.push(hours);
        }

        // Time to schedule
        if (referral?.scheduled_at) {
          const hours = differenceInHours(
            new Date(referral.scheduled_at),
            new Date(referral.submission_date)
          );
          timeToSchedule?.push(hours);
        }

        // Time to complete
        if (referral?.completed_at) {
          const hours = differenceInHours(
            new Date(referral.completed_at),
            new Date(referral.submission_date)
          );
          timeToComplete?.push(hours);
        }
      });

      const calculateStats = (times) => {
        if (times?.length === 0) return { avg: 0, min: 0, max: 0, median: 0 };
        
        const sorted = [...times]?.sort((a, b) => a - b);
        return {
          avg: times?.reduce((a, b) => a + b, 0) / times?.length,
          min: sorted?.[0],
          max: sorted?.[sorted?.length - 1],
          median: sorted?.[Math.floor(sorted?.length / 2)]
        };
      };

      return {
        responseTime: calculateStats(responseTimes),
        timeToSchedule: calculateStats(timeToSchedule),
        timeToComplete: calculateStats(timeToComplete),
        totalProcessed: data?.length || 0
      };
    } catch (error) {
      console.error('Error fetching response time metrics:', error);
      throw error;
    }
  },

  /**
   * Get workflow insights
   */
  async getWorkflowInsights(practitionerId, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase?.from('referrals')?.select('*')?.gte('submission_date', startDate?.toISOString());

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const insights = {
        totalReferrals: data?.length || 0,
        statusDistribution: {
          new: 0,
          reviewed: 0,
          scheduled: 0,
          completed: 0,
          cancelled: 0
        },
        urgencyDistribution: {
          routine: 0,
          urgent: 0,
          emergency: 0
        },
        completionRate: 0,
        cancellationRate: 0,
        avgDaysToComplete: 0
      };

      let completionDays = [];

      data?.forEach(referral => {
        // Status distribution
        insights.statusDistribution[referral.status]++;
        
        // Urgency distribution
        insights.urgencyDistribution[referral.urgency]++;
        
        // Days to complete
        if (referral?.completed_at) {
          const days = differenceInDays(
            new Date(referral.completed_at),
            new Date(referral.submission_date)
          );
          completionDays?.push(days);
        }
      });

      // Calculate rates
      if (insights?.totalReferrals > 0) {
        insights.completionRate = 
          (insights?.statusDistribution?.completed / insights?.totalReferrals) * 100;
        insights.cancellationRate = 
          (insights?.statusDistribution?.cancelled / insights?.totalReferrals) * 100;
      }

      // Average days to complete
      if (completionDays?.length > 0) {
        insights.avgDaysToComplete = 
          completionDays?.reduce((a, b) => a + b, 0) / completionDays?.length;
      }

      return insights;
    } catch (error) {
      console.error('Error fetching workflow insights:', error);
      throw error;
    }
  },

  /**
   * Get statistics summary for a practitioner
   */
  async getStatistics(practitionerId) {
    try {
      let query = supabase?.from('referrals')?.select('status');

      // Filter by practitioner only if practitionerId is provided
      if (practitionerId) {
        query = query?.eq('practitioner_id', practitionerId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const statistics = {
        total: data?.length || 0,
        new: 0,
        reviewed: 0,
        scheduled: 0,
        completed: 0,
        cancelled: 0,
        pending: 0
      };

      data?.forEach(referral => {
        const status = referral?.status;
        if (statistics?.hasOwnProperty(status)) {
          statistics[status]++;
        }
        
        // Calculate pending (new + reviewed)
        if (status === 'new' || status === 'reviewed') {
          statistics.pending++;
        }
      });

      return statistics;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
};